import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CollaboratorRateRequest,
  type CollaboratorRateResponse,
  collaboratorRateSchema,
  type EmployeeResponse,
  formatMoney,
  formatQuantity,
  RATE_UNIT_LABELS,
  RATE_UNITS,
  type RateUnit,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';
import { peopleKeys } from './people-ui.js';

const ratesKey = (id: string) => [...peopleKeys.employee(id), 'rates'] as const;

/**
 * The card of a collaborator › «Costo» (2.38): the cost typed by hand, by the
 * hour or by the day, each valid until the next one. The hours, and so the
 * cost of a period, come with K-Presenze. Only with «persone.costs».
 */
export function CollaboratorRates({ employee }: { employee: EmployeeResponse }) {
  const queryClient = useQueryClient();
  const canWrite = useCan('persone.write');
  const [editing, setEditing] = useState<CollaboratorRateResponse | 'new' | null>(null);
  const [removing, setRemoving] = useState<CollaboratorRateResponse | null>(null);
  const rates = useQuery({
    queryKey: ratesKey(employee.id),
    queryFn: () => apiRequest<CollaboratorRateResponse[]>(`/people/employees/${employee.id}/rates`),
  });
  const remove = useMutation({
    mutationFn: (rate: CollaboratorRateResponse) =>
      apiRequest<CollaboratorRateResponse[]>(`/people/rates/${rate.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setRemoving(null);
      await queryClient.invalidateQueries({ queryKey: peopleKeys.all });
    },
  });
  const current = rates.data?.find((rate) => rate.validTo === null) ?? null;

  return (
    <div className="stack">
      <div className="kpi-row">
        <div className="card kpi">
          <small className="muted">€/ora in vigore</small>
          <strong>{current ? formatMoney(current.hourly) : '—'}</strong>
          <small className="muted">
            {current ? `dal ${formatDate(current.validFrom)}` : 'nessun costo digitato'}
          </small>
        </div>
        <div className="card kpi">
          <small className="muted">Ore e costo del periodo</small>
          <strong className="muted">—</strong>
          <small className="muted">arriveranno con K-Presenze</small>
        </div>
      </div>
      <div className="card stack">
        <div className="row between">
          <h2>Costo digitato</h2>
          {canWrite && (
            <button type="button" className="primary" onClick={() => setEditing('new')}>
              Nuovo costo…
            </button>
          )}
        </div>
        {rates.isError && <p className="error">{rates.error.message}</p>}
        {rates.data && rates.data.length === 0 && (
          <p className="muted">
            Nessun costo: digita quanto costa il collaboratore all’ora o al giorno, cassa compresa e
            IVA esclusa.
          </p>
        )}
        {rates.data && rates.data.length > 0 && (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Dal</th>
                  <th>Al</th>
                  <th className="numeric">Costo</th>
                  <th className="numeric">Ore al giorno</th>
                  <th className="numeric">€/ora</th>
                  <th>Note</th>
                  {canWrite && <th />}
                </tr>
              </thead>
              <tbody>
                {[...rates.data].reverse().map((rate) => (
                  <tr key={rate.id}>
                    <td>{formatDate(rate.validFrom)}</td>
                    <td>{rate.validTo ? formatDate(rate.validTo) : 'in vigore'}</td>
                    <td className="numeric">
                      {formatMoney(rate.amount)} {RATE_UNIT_LABELS[rate.unit]}
                    </td>
                    <td className="numeric">
                      {rate.hoursPerDay ? formatQuantity(rate.hoursPerDay) : '—'}
                    </td>
                    <td className="numeric">
                      <strong>{formatMoney(rate.hourly)}</strong>
                    </td>
                    <td>{rate.notes ?? ''}</td>
                    {canWrite && (
                      <td className="row">
                        <button
                          type="button"
                          className="link small"
                          onClick={() => setEditing(rate)}
                        >
                          Modifica
                        </button>
                        <button
                          type="button"
                          className="link small"
                          onClick={() => setRemoving(rate)}
                        >
                          Togli
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted small">
          Ogni costo vale dal suo giorno fino al giorno prima del successivo. Le fatture del
          fornitore collegato si vedono in Tesoreria e restano solo un’informazione: non entrano nel
          calcolo.
        </p>
      </div>
      {editing && (
        <RateDialog
          employeeId={employee.id}
          rate={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="Togliere questo costo?"
          confirmLabel="Togli"
          busy={remove.isPending}
          error={remove.isError ? remove.error.message : undefined}
          onConfirm={() => remove.mutate(removing)}
          onCancel={() => setRemoving(null)}
        >
          <p>
            Il costo dal {formatDate(removing.validFrom)} viene tolto: vale di nuovo il precedente.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function RateDialog({
  employeeId,
  rate,
  onClose,
}: {
  employeeId: string;
  rate: CollaboratorRateResponse | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [validFrom, setValidFrom] = useState(rate?.validFrom ?? '');
  const [unit, setUnit] = useState<RateUnit>(rate?.unit ?? 'ora');
  const [amount, setAmount] = useState(rate ? rate.amount.replace('.', ',') : '');
  const [hoursPerDay, setHoursPerDay] = useState(rate?.hoursPerDay?.replace('.', ',') ?? '8');
  const [notes, setNotes] = useState(rate?.notes ?? '');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const save = useMutation({
    mutationFn: (body: CollaboratorRateRequest) =>
      rate
        ? apiRequest<CollaboratorRateResponse[]>(`/people/rates/${rate.id}`, {
            method: 'PUT',
            body,
          })
        : apiRequest<CollaboratorRateResponse[]>(`/people/employees/${employeeId}/rates`, {
            method: 'POST',
            body,
          }),
    onError: (error) => showServerError(error, setErrors, setFormError),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: peopleKeys.all });
      onClose();
    },
  });
  const body: CollaboratorRateRequest = {
    validFrom,
    unit,
    amount,
    hoursPerDay: unit === 'giorno' ? hoursPerDay : null,
    notes,
  };
  const checked = collaboratorRateSchema.safeParse(body);

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>{rate ? 'Modifica il costo' : 'Nuovo costo'}</h2>
        <div className="form-grid">
          <Field label="Vale dal" error={errors.validFrom}>
            <input
              type="date"
              value={validFrom}
              onChange={(event) => setValidFrom(event.target.value)}
            />
          </Field>
          <Field label="Costo">
            <select value={unit} onChange={(event) => setUnit(event.target.value as RateUnit)}>
              {RATE_UNITS.map((item) => (
                <option key={item} value={item}>
                  {RATE_UNIT_LABELS[item]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Importo (€)" error={errors.amount} hint="Cassa compresa, IVA esclusa.">
            <input
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </Field>
          {unit === 'giorno' && (
            <Field label="Ore al giorno" error={errors.hoursPerDay}>
              <input
                inputMode="decimal"
                value={hoursPerDay}
                onChange={(event) => setHoursPerDay(event.target.value)}
              />
            </Field>
          )}
          <Field label="Note" error={errors.notes}>
            <input value={notes} onChange={(event) => setNotes(event.target.value)} />
          </Field>
        </div>
        {checked.success && (
          <p className="muted">
            €/ora:{' '}
            <strong>
              {formatMoney(
                checked.data.unit === 'ora'
                  ? checked.data.amount
                  : (Number(checked.data.amount) / Number(checked.data.hoursPerDay)).toFixed(2),
              )}
            </strong>
          </p>
        )}
        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={save.isPending}
            onClick={() => {
              const result = validate(collaboratorRateSchema, body);
              setErrors(result.errors);
              setFormError(
                Object.keys(result.errors).length ? 'Correggi i campi segnati in rosso.' : '',
              );
              if (!Object.keys(result.errors).length) save.mutate(body);
            }}
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
